import { omit } from 'lodash';

import { createReducer } from 'utils/redux';
import { API, get, put, request } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { receiveAssets, resetAssets } from 'modules/campaigns/reducers/campaignAssets';

import {
  trackEventCampaignCreated,
  trackEventCampaignEdited
} from '../../tracking/utils/tracking';

applyTranslations(require('../components/campaign-brief-form/campaign-brief-form.i18n.json'));

const RECEIVE_CAMPAIGN_BRIEF = 'campaignBrief/RECEIVE_CAMPAIGN_BRIEF';
const SET_BUDGET_NOTE = 'campaignBrief/SET_BUDGET_NOTE';
const RESET_CAMPAIGN_BRIEF = 'campaignBrief/RESET_CAMPAIGN_BRIEF';
const STORE_BRIEF_DETAILS = 'campaignBrief/STORE_BRIEF_DETAILS';
const DEFAULT_TEMPLATE_UPDATED = 'campaignBrief/DEFAULT_TEMPLATE_UPDATED';

const { CAMPAIGNS } = API.END_POINTS;
const { getBrands, getMerchants } = API.END_POINTS.API;

const initialBrief = {
  id: null,
  name: '',
  description: '',
  startDate: null,
  endDate: null,
  budget: null,
  mainImageId: null,
  defaultSubcampaignTemplate: null
};

const initialState = {
  brief: {
    ...initialBrief
  }
};

const MSG_KEY = 'campaign_brief_form';

export function receiveBrief(data) {
  return {
    type: RECEIVE_CAMPAIGN_BRIEF,
    payload: {
      data
    }
  };
}

export function setBudgetNote(budgetNote) {
  return {
    type: SET_BUDGET_NOTE,
    payload: {
      budgetNote
    }
  };
}

export function defaultSubcampaignTemplateUpdated(data) {
  return {
    type: DEFAULT_TEMPLATE_UPDATED,
    payload: {
      data
    }
  };
}

function resetBrief() {
  return {
    type: RESET_CAMPAIGN_BRIEF,
    payload: {}
  };
}

export function resetCampaignBrief() {
  return (dispatch) => {
    dispatch(resetBrief());
    dispatch(resetAssets());
  };
}

export function fetchCampaignBrief(campaignId, showLoader = true, fullReset = true) {
  const url = `${CAMPAIGNS.getCampaign(campaignId)}?include=main_image,assets,managers`;

  return (dispatch) => {
    if (showLoader) {
      dispatch(showLoading(url));
    }

    if (fullReset) {
      dispatch(resetCampaignBrief());
    }

    return get(url)
      .then((response) => {
        const campaign = response.data;

        dispatch(receiveBrief(campaign));
        dispatch(receiveAssets(campaign.assets, campaign.mainImage));

        if (showLoader) {
          dispatch(hideLoading(url));
        }

        return campaign;
      })
      .catch((error) => {
        dispatch(danger(__t(`${MSG_KEY}.errors.campaign_load`)));

        if (showLoader) {
          dispatch(hideLoading(url));
        }

        throw error;
      });
  };
}

export function updateBudgetNote(campaignId, newNote) {
  return async (dispatch) => {
    const url = CAMPAIGNS.campaignOverviewBudgetNote(campaignId, newNote);

    dispatch(showLoading(url));

    try {
      await put(url);
      dispatch(setBudgetNote(newNote));
    } catch (e) {
      logError(e);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function saveCampaignBrief(profileId, brief, shouldSendNotifications = false) {
  return (dispatch, getState) => {
    const shouldUpdate = Boolean(brief.id);
    const method = shouldUpdate ? 'PUT' : 'POST';

    const isZalandoMerchantsFieldHidden = getState().runtimeConfig.config?.isZalandoMerchantsFieldHidden;

    const url = brief.id ?
      `${CAMPAIGNS.updateCampaign(brief.id)}${shouldSendNotifications ? '?notify_influencers=true' : ''}` :
      CAMPAIGNS.createCampaign(profileId);

    dispatch(showLoading(url));

    return request(method, url, {
      data: {
        ...omit(brief, ['assets', 'organization', 'brandProfile', 'state', 'mainImage', 'managers', 'managerIds', 'zalandoMeta']),
        managersIds: brief.managers.map(manager => manager.value),
        zalandoMeta: {
          brands: brief?.zalandoMeta?.brands,
          merchants: isZalandoMerchantsFieldHidden ? [] : brief?.zalandoMeta?.merchants
        },
        campaignType: 'collab'
      }
    })
      .then((response) => {
        // Be aware of the whole stored Campaign being replaced
        const campaign = response.data;

        dispatch(receiveBrief(campaign));

        if (shouldUpdate) {
          trackEventCampaignEdited(brief);
        } else {
          trackEventCampaignCreated(campaign);
        }

        dispatch(hideLoading(url));

        return campaign;
      })
      .catch((error) => {
        const errorMsgByCode = __t(`${MSG_KEY}.errors.${error?.response?.data?.code}`);
        const errorMsg = errorMsgByCode || __t(`${MSG_KEY}.errors.campaign_save`);

        dispatch(danger(errorMsg, { keepBetweenPages: true }));
        dispatch(hideLoading(url));

        throw error;
      });
  };
}

export function editCampaignBrief(shouldSendNotifications) {
  return (dispatch, getState) => {
    const brief = getState().campaignBrief.brief;

    return dispatch(saveCampaignBrief(null, brief, shouldSendNotifications));
  };
}

export function storeBriefDetails(briefDetails) {
  return {
    type: STORE_BRIEF_DETAILS,
    payload: {
      briefDetails
    }
  };
}

export const fetchCampaignMetaFieldsData = async (metaDataFieldType, query) => {
  try {
    const url = metaDataFieldType === 'brands' ? getBrands(query) : getMerchants(query);

    const response = await get(url);

    return response.data;
  } catch (error) {
    logError(error);

    return [];
  }
};

export function searchToDropdownItems(query, type) {
  return fetchCampaignMetaFieldsData(type, query)
    .then((items) =>
      items.map((item) => ({
        identifier: item.isFamily ? `${item.name}--${item.familyId}` : (item.code || item.id),
        isDisabled: false,
        isFocused: false,
        isSelected: false,
        label: `${type === 'brands' ? item.name : `${item.name} - ${item.companyName}`}${item.isFamily ? ' (All)' : ''}`,
        value: item.code || item.id,
        data: item.subBrands
      }))
    );
}

export default createReducer('campaignBrief', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RECEIVE_CAMPAIGN_BRIEF]: (state, { data }) => ({
    ...state,
    brief: data
  }),
  [RESET_CAMPAIGN_BRIEF]: (state) => ({
    ...state,
    brief: {
      ...initialBrief
    }
  }),
  [STORE_BRIEF_DETAILS]: (state, { briefDetails }) => ({
    ...state,
    brief: {
      ...state.brief,
      ...briefDetails
    }
  }),
  [SET_BUDGET_NOTE]: (state, { budgetNote }) => ({
    ...state,
    brief: {
      ...state.brief,
      budgetNote
    }
  }),
  [DEFAULT_TEMPLATE_UPDATED]: (state, { data }) => ({
    ...state,
    brief: {
      ...state.brief,
      defaultSubcampaignTemplate: data
    }
  })
});
